import React, { useState } from 'react';

const DebugAnalytics = () => {
  const [testResults, setTestResults] = useState({});
  const [loading, setLoading] = useState(false);

  const runTests = async () => {
    setLoading(true);
    const results = {};

    // Test 1: API endpoint
    try {
      console.log('🧪 Testing /api/analytics endpoint...');
      const response = await fetch('/api/analytics', { method: 'GET' });
      results.apiTest = {
        status: response.status,
        ok: response.ok,
        data: response.ok ? await response.json() : null,
        error: response.ok ? null : `HTTP ${response.status}`
      };
    } catch (error) {
      results.apiTest = {
        status: 'ERROR',
        ok: false,
        data: null,
        error: error.message
      };
    }

    // Test 2: Static JSON file
    try {
      console.log('🧪 Testing /dev-analytics.json file...');
      const response = await fetch('/dev-analytics.json');
      results.jsonTest = {
        status: response.status,
        ok: response.ok,
        data: response.ok ? await response.json() : null,
        error: response.ok ? null : `HTTP ${response.status}`
      };
    } catch (error) {
      results.jsonTest = {
        status: 'ERROR',
        ok: false,
        data: null,
        error: error.message
      };
    }

    // Test 3: Demo data generation
    try {
      console.log('🧪 Testing demo data generation...');
      const demoData = generateDemoData();
      results.demoTest = {
        status: 'OK',
        ok: true,
        data: demoData,
        error: null
      };
    } catch (error) {
      results.demoTest = {
        status: 'ERROR',
        ok: false,
        data: null,
        error: error.message
      };
    }

    setTestResults(results);
    setLoading(false);
  };

  const generateDemoData = () => ({
    summary: {
      totalVisitors: 42,
      totalSessions: 67,
      totalPageViews: 156,
      totalEvents: 890,
      lastUpdated: new Date().toISOString()
    },
    recentSessions: [
      JSON.stringify({
        sessionId: 'debug-1',
        userId: 'debug-user-1',
        startTime: Date.now() - 300000,
        locationInfo: { city: 'Debug City', country: 'Debug Country' },
        deviceInfo: { deviceType: 'desktop', browser: { browser: 'Chrome' } },
        summary: 'Debug City - desktop'
      })
    ],
    stats: {
      locations: { 'Debug Country': '1' },
      devices: { 'desktop': '1' },
      browsers: { 'Chrome': '1' }
    }
  });

  return (
    <div className="p-8 max-w-4xl mx-auto bg-gray-50 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Analytics Debug Dashboard
        </h1>
        <p className="text-gray-600">
          Test different analytics data sources to identify issues
        </p>
      </div>

      <div className="mb-6">
        <button
          onClick={runTests}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50"
        >
          {loading ? 'Running Tests...' : 'Run Analytics Tests'}
        </button>
      </div>

      {Object.keys(testResults).length > 0 && (
        <div className="space-y-6">
          {/* API Test Results */}
          <TestResult
            title="API Endpoint Test (/api/analytics)"
            result={testResults.apiTest}
            description="Tests if the Vercel serverless function is working"
          />

          {/* JSON File Test Results */}
          <TestResult
            title="Static JSON Test (/dev-analytics.json)"
            result={testResults.jsonTest}
            description="Tests if the development JSON file is accessible"
          />

          {/* Demo Data Test Results */}
          <TestResult
            title="Demo Data Generation Test"
            result={testResults.demoTest}
            description="Tests if demo data can be generated successfully"
          />
        </div>
      )}
    </div>
  );
};

const TestResult = ({ title, result, description }) => {
  const isSuccess = result?.ok;
  const bgColor = isSuccess ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
  const textColor = isSuccess ? 'text-green-800' : 'text-red-800';
  const icon = isSuccess ? '✅' : '❌';

  return (
    <div className={`border rounded-lg p-6 ${bgColor}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xl">{icon}</span>
        <h3 className={`text-lg font-semibold ${textColor}`}>{title}</h3>
      </div>
      
      <p className={`text-sm mb-4 ${textColor} opacity-80`}>{description}</p>
      
      <div className="space-y-2">
        <div className={`text-sm ${textColor}`}>
          <strong>Status:</strong> {result?.status || 'Not tested'}
        </div>
        
        {result?.error && (
          <div className={`text-sm ${textColor}`}>
            <strong>Error:</strong> {result.error}
          </div>
        )}
        
        {result?.data && (
          <details className="mt-4">
            <summary className={`cursor-pointer font-medium ${textColor}`}>
              View Data ({result.data.recentSessions?.length || 0} sessions)
            </summary>
            <pre className="mt-2 text-xs bg-white p-3 rounded border overflow-x-auto">
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
};

export default DebugAnalytics; 